import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { focusElement } from 'platform/utilities/ui';

import FormNavButtons from '~/platform/forms-system/src/js/components/FormNavButtons';

import CatAndTopicSummary from '../components/CatAndTopicSummary';
import Loader from '../components/Loader';
import { ServerErrorAlert } from '../config/helpers';
import { CHAPTER_1, URL, getApiUrl } from '../constants';

const TopicSelectPage = props => {
  const { onChange, isLoggedIn, goBack, goForward, formData, categoryId } =
    props;

  const isLOA3 = useSelector(state => state.user.profile.loa.current === 3);

  const [apiData, setApiData] = useState([]);
  const [loading, isLoading] = useState(false);
  const [error, hasError] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const handleContinue = () => {
    if (formData.selectTopic) {
      return goForward(formData);
    }

    setValidationError('Select a topic');
    return focusElement('va-radio-option input[type="radio"]');
  };

  const handleChange = event => {
    const selectedValue = event.detail.value;

    if (!selectedValue || selectedValue === '') {
      onChange({
        ...formData,
        selectTopic: '',
        topicId: null,
        topicHasSubtopics: false,
        topicAllowAttachments: false,
        topicRequiresSignIn: false,
        requireSignInLogic: {
          ...formData.requireSignInLogic,
          topic: false,
        },
      });
      return;
    }

    const selected = apiData.find(
      topic => topic.attributes.name === selectedValue,
    );

    if (!selected) return;

    onChange({
      ...formData,
      contactPreferences: selected.attributes.contactPreferences,
      selectTopic: selectedValue,
      topicId: selected.id,
      topicAllowAttachments: selected.attributes.allowAttachments,
      topicHasSubtopics: selected.attributes.hasSubtopics,
      topicRequiresSignIn:
        selected.attributes.requiresAuthentication && !isLOA3,
      requireSignInLogic: formData.requireSignInLogic
        ? {
            ...formData.requireSignInLogic,
            topic: selected.attributes.requiresAuthentication,
          }
        : {
            category: false,
            topic: selected.attributes.requiresAuthentication,
            personQuestionIsAbout: false,
          },
    });
  };

  const getApiData = url => {
    isLoading(true);
    return apiRequest(url)
      .then(res => {
        setApiData(res.data);
        isLoading(false);
      })
      .catch(() => {
        isLoading(false);
        hasError(true);
      });
  };

  useEffect(() => {
    getApiData(getApiUrl(URL.GET_TOPICS, { PARENT_ID: categoryId }));
  }, [isLoggedIn, categoryId]);

  useEffect(() => {
    focusElement('h2');
  }, [loading]);

  // render loading indicator while we fetch
  if (loading) return <Loader />;

  return !error ? (
    <>
      {CatAndTopicSummary({ category: formData.selectCategory })}
      <form className="rjsf" data-dd-privacy="allow">
        <VaRadio
          onVaValueChange={handleChange}
          label={CHAPTER_1.PAGE_2.QUESTION_1}
          label-header-level={3}
          error={validationError}
          name="root_selectTopic"
          required
          uswds
        >
          {apiData?.map(topic => (
            <VaRadioOption
              key={topic.id}
              name="root_selectTopic"
              id={topic.id}
              value={topic.attributes.name}
              label={topic.attributes.displayName}
              checked={topic.attributes.name === formData.selectTopic}
              uswds
            />
          ))}
        </VaRadio>

        <FormNavButtons goBack={goBack} goForward={handleContinue} />
      </form>
    </>
  ) : (
    <ServerErrorAlert />
  );
};

TopicSelectPage.propTypes = {
  categoryId: PropTypes.string,
  formData: PropTypes.object,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  id: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    isLoggedIn: state.user.login.currentlyLoggedIn,
    formData: state.form.data,
    categoryId: state.form.data.categoryId,
  };
}

export default connect(mapStateToProps)(TopicSelectPage);
